import React, { useEffect, useState } from 'react';
import { ClickAwayListener, Grid, Popper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  Filter,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../atoms/ReportsCohortItem';
import AutocompleteWithCheckBoxes from '../../atoms/Autocomplete';
import {} from '@HomaHealth/ui-component-library';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import { TextField } from '@HomaHealth/ui-component-library/lib/_patient-access-components';

interface FilterLevel {
  label: string;
   
  options: string[] | ((previousValue: string) => string[]); // Can be static or dynamic based on previous selection
  isMultiSelect?: boolean;
}

export interface FilterOption {
  label: string;
  options: string[];
  selected: string | string[] | null;
   
  onChange: (value: any) => void;
  isSelectMultiple?: boolean;
  isSingleLevel?: boolean;
  // NEW: Add levels configuration
  levels?: FilterLevel[];
}

interface DynamicFilterProps {
  filterOptions: FilterOption[];
  resetKey?: number;
}

const useStyles = makeStyles()({
  filterContainer: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
});

export const DynamicFilter: React.FC<DynamicFilterProps> = ({
  filterOptions,
  resetKey,
}) => {
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [levelSelections, setLevelSelections] = useState<{
    [filterLabel: string]: string[];
  }>({});

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'multi-filter-popper' : undefined;

  const getDisplayText = (
    label: string,
    selected: string | string[] | null,
  ) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      return label;
    }

    const valueText = Array.isArray(selected) ? selected.join(', ') : selected;

    return `${label} :- ${valueText}`;
  };

  const handleLevelChange = (
    filterLabel: string,
    levelIndex: number,
    value: string | string[],
    filter: FilterOption,
  ) => {
    const currentSelections = levelSelections[filterLabel] || [];
    const updatedSelections = [...currentSelections];

    updatedSelections[levelIndex] = Array.isArray(value)
      ? value.join(', ')
      : value;

    const finalSelections = updatedSelections.slice(0, levelIndex + 1);

    setLevelSelections({
      ...levelSelections,
      [filterLabel]: finalSelections,
    });
    filter.onChange(finalSelections);
  };

  const getCurrentLevelOptions = (
    filter: FilterOption,
    levelIndex: number,
  ): string[] => {
    if (!filter.levels || !filter.levels[levelIndex]) {
      return [];
    }

    const level = filter.levels[levelIndex];

    // If options is a function, call it with the previous level's selection
    if (typeof level.options === 'function') {
      const previousValue =
        levelSelections[filter.label]?.[levelIndex - 1] || '';
      return level.options(previousValue);
    }

    // Otherwise, return static options
    return level.options;
  };

  const renderFilterLevels = (filter: FilterOption) => {
    if (!filter.levels || filter.levels.length === 0) {
      return null;
    }

    const currentSelections = levelSelections[filter.label] || [];

    return (
      <>
        {filter.levels.map((level, levelIndex) => {
          // Only show this level if the previous level has a selection
          // (or if this is the first level)
          const shouldShow =
            levelIndex === 0 || currentSelections[levelIndex - 1];

          if (!shouldShow) {
            return null;
          }

          const levelOptions = getCurrentLevelOptions(filter, levelIndex);
          const currentValue = currentSelections[levelIndex] || '';

          return (
            <React.Fragment key={`${filter.label}-level-${levelIndex}`}>
              {level.isMultiSelect ? (
                <AutocompleteWithCheckBoxes
                  options={levelOptions}
                  defaultValue={currentValue ? currentValue.split(', ') : []}
                  label={level.label}
                  handleChange={(_, value) => {
                    handleLevelChange(filter.label, levelIndex, value, filter);
                  }}
                  width="300px"
                />
              ) : (
                <Dropdown
                  label={level.label}
                  dropDownOptions={levelOptions}
                  dropDownValues={levelOptions}
                  value={currentValue}
                  selectStyles={{
                    width: '300px',
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'unset',
                  }}
                  handleChange={(e) =>
                    handleLevelChange(
                      filter.label,
                      levelIndex,
                      e.target.value as string,
                      filter,
                    )
                  }
                  width="300px"
                  renderValue={(selected: any) =>
                    getDisplayText(level.label, selected as string)
                  }
                />
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  useEffect(() => {
    if (resetKey !== undefined) {
      setActiveFilter(null);
      setAnchorEl(null);
      setLevelSelections({});
    }
  }, [resetKey]);

  return (
    <Grid className={classes.filterContainer}>
      <Button
        aria-describedby={id}
        label="Add Filter"
        startIcon={<Filter />}
        onClick={handleClick}
        height="37px"
      />
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ marginTop: '0.25rem', zIndex: 2000, width: '180px' }}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Dropdown
            label="Select Filter Type"
            placeholder="Select Filter Type"
            dropDownOptions={filterOptions.map((f) => f.label)}
            dropDownValues={filterOptions.map((f) => f.label)}
            value={activeFilter || ''}
            handleChange={(event) => {
              const selectedLabel = event.target.value as string;
              const selectedFilter = filterOptions.find(
                (f) => f.label === selectedLabel,
              );

              setActiveFilter(selectedLabel);
              if (selectedFilter && selectedFilter.levels?.length === 1) {
                selectedFilter.onChange([]);
              }

              setLevelSelections({
                ...levelSelections,
                [selectedLabel]: [],
              });
              setAnchorEl(null);
            }}
            style={{ width: '180px' }}
            selectStyles={{
              ...commonDropDownProps.selectStyles,
              width: '100%',
            }}
            labelTop="10px"
          />
        </ClickAwayListener>
      </Popper>
      {activeFilter &&
        filterOptions
          .filter((filter) => filter.label === activeFilter)
          .map((filter) => (
            <React.Fragment key={filter.label}>
              {/* 🔹 SINGLE LEVEL FILTER */}
              {filter.isSingleLevel ? (
                <ReportCreateNewLabelField
                  label=""
                  component={
                    <TextField
                      name=""
                      value={`Filtered By :- ${filter.label}`}
                      placeholder=""
                      height="36px"
                      width="301px"
                      inputProps={{ readOnly: true }}
                    />
                  }
                />
              ) : filter.levels && filter.levels.length > 0 ? (
                /* 🔹 DYNAMIC MULTI-LEVEL FILTER */
                renderFilterLevels(filter)
              ) : filter.isSelectMultiple ? (
                /* 🔹 MULTI SELECT FILTER (LEGACY) */
                <AutocompleteWithCheckBoxes
                  options={filter.options}
                  defaultValue={(filter?.selected as string[]) ?? []}
                  label={filter.label}
                  handleChange={(_, value) => {
                    filter.onChange(value);
                  }}
                  width="300px"
                />
              ) : (
                /* 🔹 NORMAL TWO-LEVEL SINGLE SELECT (LEGACY) */
                <Dropdown
                  label={filter.label}
                  dropDownOptions={filter.options}
                  dropDownValues={filter.options}
                  value={(filter.selected as string) ?? ''}
                  handleChange={(e) =>
                    filter.onChange(e.target.value as string)
                  }
                  width="300px"
                  renderValue={(selected: any) =>
                    getDisplayText(filter.label, selected as string)
                  }
                  selectStyles={{
                    width: '300px',
                  }}
                />
              )}
            </React.Fragment>
          ))}
    </Grid>
  );
};
